/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
* Custom Video
*/
class CustomVideo extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Extract attributes
    const videoUrl = this.getAttribute('data-video-id');
    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const loop = this.getAttribute('data-loop') === 'true';
    const controls = this.getAttribute('data-controls') === 'true';
    const muted = this.getAttribute('data-muted') === 'true';

    // Extract the video ID from the YouTube URL
    const videoId = this.extractVideoId(videoUrl);

    // Create video wrapper
    const wrapper = document.createElement('div');
    wrapper.classList.add('custom-video-wrapper');
    
    // Create video element (iframe)
    const videoFrame = document.createElement('iframe');
    videoFrame.src = `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&loop=${loop ? 1 : 0}&controls=${controls ? 1 : 0}&mute=${muted ? 1 : 0}&playlist=${loop ? videoId : ''}`;
    videoFrame.allow = "autoplay; encrypted-media";
    videoFrame.setAttribute('frameborder', '0');
    videoFrame.setAttribute('allowfullscreen', '');

    // Create preview image
    const previewImage = document.createElement('img');
    previewImage.src = `//img.youtube.com/vi/${videoId}/0.jpg`;
    previewImage.alt = this.getAttribute('data-video-alt') || 'Video preview';
    previewImage.loading = 'lazy';
    previewImage.classList.add('video-preview');

    // Create play button
    const playButtonWrapper = document.createElement('div');
    playButtonWrapper.classList.add('cta-btn');
    const playButton = document.createElement('button');
    playButton.classList.add('play__button', 'btn', 'btn-primary');
    playButton.setAttribute('aria-label', 'Play Video');
    playButton.innerHTML = `<span class="icon-play"></span>`;

    // Append play button to the wrapper
    playButtonWrapper.appendChild(playButton);

    // Play button click event: replace image with video
    playButton.addEventListener('click', () => {
      previewImage.replaceWith(videoFrame);
      playButtonWrapper.remove();  // Remove the play button after playing the video
    });

    // Append image and button to the wrapper
    wrapper.appendChild(previewImage);
    wrapper.appendChild(playButtonWrapper);
    this.shadowRoot.appendChild(wrapper);

    // Apply basic styles for the custom element
    const style = document.createElement('style');
    style.textContent = `
      .custom-video-wrapper {
        position: relative;
        display: inline-block;
      }
      .custom-video-wrapper > iframe {
        width: 470px;
        height: 270px;
      }
      .video-preview {
        cursor: pointer;
        display: block;
        width: 100%;
        height: auto;
      }
      .cta-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .play__button {
        background: rgba(0, 0, 0, 0.5);
        border: none;
        color: #fff;
        font-size: 2rem;
        border-radius: 50%;
        padding: 0.5rem 1rem;
        cursor: pointer;
      }
      .play__button:hover {
        background: rgba(0, 0, 0, 0.7);
      }
      .icon-play::before {
        content: '▶'; /* Simple play icon */
      }
      /* Extra small devices (phones, 0 - 600px) */
      @media only screen and (max-width: 600px) {
        .custom-video-wrapper > iframe {
          width: 100%;
        }
      }
      
      /* Small devices (portrait tablets, 600px and up) */
      @media only screen and (min-width: 600px) and (max-width: 768px) {
        /* Styles for portrait tablets */
      }
      
      /* Medium devices (landscape tablets, 768px and up) */
      @media only screen and (min-width: 768px) and (max-width: 992px) {
        /* Styles for landscape tablets */
      }
      
      /* Large devices (laptops/desktops, 992px and up) */
      @media only screen and (min-width: 992px) and (max-width: 1200px) {
        /* Styles for desktops */
      }
      
      /* Extra large devices (large desktops, 1200px and up) */
      @media only screen and (min-width: 1200px) {
        /* Styles for large desktops */
      }

    `;
    this.shadowRoot.appendChild(style);
  }

  // Method to extract the video ID from the full YouTube URL
  extractVideoId(url) {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('v'); // Extract 'v' parameter from URL
  }
}

// Define the custom element
customElements.define('custom-video', CustomVideo);

// Homepage Hero Slider
const heroSlide = document.querySelectorAll(".header-slider-desktop .swiper-slide");
const slideTransitionDuration = 500; // Set your desired transition duration in milliseconds
const slideChangeDelay = 5000; // Set the delay between slide changes in milliseconds

var heroSlider = new Swiper(".header-slider-desktop", {
  loop: true,
  watchSlidesProgress: true,
  effect: "fade",
  fadeEffect: {
    crossFade: true,
  },
  autoplay: {
    delay: slideTransitionDuration + slideChangeDelay,
    disableOnInteraction: true,
  },
  speed: slideTransitionDuration, // Set the transition speed
  pagination: {
    el: ".slide-swiper-pagination",
    clickable: true,
  },
  navigation: {
    nextEl: ".hero-button-next",
    prevEl: ".hero-button-prev",
  },
  on: {
    init: function () {
      var slideIndex = this.realIndex;
      sliderInt(slideIndex);
    },
    slideChangeTransitionStart: function () {
      var slideIndex = this.realIndex;
      sliderInt(slideIndex);
    },
  }
});

function sliderInt(slideIndex) {
  heroSlide.forEach((img) => {
    const index = img.getAttribute("data-swiper-slide-index") || img.getAttribute("data-index");
    if (index == slideIndex || heroSlide.length === 1) {
      img.classList.add("activeBG");
    } else {
      img.classList.remove("activeBG");
    }
  });

  // GSAP animation
  if (document.querySelector(".activeBG .target_animate")) {
    gsap.set(".activeBG .target_animate", { opacity: 0, clipPath: "inset(100% 0 0 0)" });
    gsap.to(
      ".activeBG .target_animate",
      {
        duration: slideTransitionDuration / 1000, // Convert milliseconds to seconds
        y: 0,
        opacity: 1,
        clipPath: "inset(0% 0 0 0)",
        clearProps: "all",
        stagger: {
          amount: 0.4,
        }
      }
    );
  }
}

// Play/pause functionality for hero slider
var playPauseBtn = document.getElementById('playPauseBtn');
var isPlaying = true;
var nextSlidePlay = false;
// var remainingTIme = 0;


playPauseBtn?.addEventListener('click', function () {
  var swiperContainer = document.querySelector('.header-slider-desktop');
  swiperContainer.classList.toggle('toggled-class');
  if (isPlaying) {
    // Stop autoplay and preserve the remaining delay time
    heroSlider.autoplay.pause();
    document.querySelector('#playPauseBtn').innerHTML = '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 4.5L-3.93402e-07 9L0 -3.93402e-07L9 4.5Z" fill="white" fill-opacity="0.65"/></svg>'
  } else if(nextSlidePlay){
    nextSlidePlay = false;
    heroSlider.autoplay.start();
    document.querySelector('#playPauseBtn').innerHTML = '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="3.33443" height="9" fill="white" fill-opacity="0.65"/><rect x="5.66557" width="3.33443" height="9" fill="white" fill-opacity="0.65"/></svg>'
  } else {
    heroSlider.autoplay.resume();
    document.querySelector('#playPauseBtn').innerHTML = '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="3.33443" height="9" fill="white" fill-opacity="0.65"/><rect x="5.66557" width="3.33443" height="9" fill="white" fill-opacity="0.65"/></svg>'
  }
  isPlaying = !isPlaying;
  
});


// Pause autoplay when the tab is switched
document.addEventListener("visibilitychange", function() {
  if (document.visibilityState === 'hidden') {
    // Stop autoplay entirely
    heroSlider.autoplay.pause();
  } else {
    if (!isPlaying) {
      heroSlider.autoplay.resume();
    }
  }
});

// Listen for manual navigation (clicking next/prev buttons)
heroSlider.on('slideNextTransitionStart', function () {
  nextSlidePlay = true;
  if (!isPlaying) {
    heroSlider.autoplay.stop();
  } else {
    heroSlider.autoplay.start();
  }
});

heroSlider.on('slidePrevTransitionStart', function () {
  nextSlidePlay = true;
  if (!isPlaying) {
    heroSlider.autoplay.stop();
  } else {
    heroSlider.autoplay.start();
  }
});

